// Implements: F-UIX-020
/**
 * The owner images of a profile (logo and references), judged in the browser
 * exactly where the server would refuse them, and nowhere else.
 *
 * The server stays the authority (W2g): it sniffs the whole file, strips its
 * metadata and caps the CLEAN bytes, the pixels and the set. A check here may
 * only refuse what the server would certainly refuse, never something it would
 * store (the W1g lesson), so each one mirrors a server rule literally:
 *
 * - **Type**: for the logo only, an SVG document is let through (mockup v3 P5): the server cleans it
 *   with the shared allow-list or refuses it. Otherwise the server's sniffer starts with a signature (`_is_png`,
 *   `_jpeg_walk`, `_webp_walk`); a file without one is refused there. The
 *   first twelve bytes and the size are read here, never the name or the
 *   declared type, and never the file as text.
 * - **Size**: the route bounds the base64 TEXT at `4 * ceil(512 KiB / 3) + 4`
 *   characters (`UploadAssetBody.data`), so the same formula over the raw size
 *   is the exact door. The cap on the metadata-free bytes is the server's.
 * - **Count**: five closed keys (`logo`, `image-1`..`image-4`, `AssetKey`), so
 *   a version can never be offered a sixth image.
 * - **Alt text**: the route admits 4 x 200 characters, then `clean_text`
 *   removes control, format, surrogate and separator characters, applies NFKC
 *   and trims; the result must hold 1..200 code points (`ProfileReference`).
 */

/** `AssetKey`: the five closed keys, in the order the uploader offers them. */
export const ASSET_KEYS = ["logo", "image-1", "image-2", "image-3", "image-4"] as const;
export type AssetKey = (typeof ASSET_KEYS)[number];

/** `MAX_REFERENCE_BYTES` (P6-OD-21). */
export const MAX_REFERENCE_BYTES = 512 * 1024;
/** `MAX_ALT_TEXT_CHARS`. */
export const MAX_ALT_TEXT_CHARS = 200;
/** `RAW_HEADROOM_FACTOR`: how much longer than the limit a raw text may be at the door. */
const RAW_HEADROOM_FACTOR = 4;

export type ReferenceFileRefusal = "notSupported" | "tooBig" | "unreadable";

export type ReferenceFileRead =
  | { readonly kind: "ok"; readonly base64: string }
  | { readonly kind: "refused"; readonly reason: ReferenceFileRefusal };

export type AltTextProblem = "required" | "tooLong";

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const JPEG_SIGNATURE = [0xff, 0xd8, 0xff];
const RIFF = [0x52, 0x49, 0x46, 0x46];
const WEBP = [0x57, 0x45, 0x42, 0x50];
/** The shortest WebP the server's walk reads (`len(data) < 20` is refused). */
const MIN_WEBP_BYTES = 20;
/** Enough of the start of an SVG to pass a BOM, spaces, editor comments and an XML declaration's opening. */
const SVG_HEAD_BYTES = 1024;
const UTF8_BOM = [0xef, 0xbb, 0xbf];
const SVG_STARTS = ["<svg", "<?xml"] as const;

/**
 * Whether a file starts like an SVG document (mockup v3 P5, logo only): `<svg` or an XML
 * declaration after an optional BOM, spaces and XML comments. Only a door: the server parses it, cleans it with
 * the shared allow-list and refuses what is not a drawable SVG.
 */
function looksSvg(head: Uint8Array): boolean {
  const body = hasAt(head, 0, UTF8_BOM) ? head.subarray(UTF8_BOM.length) : head;
  let text = new TextDecoder("utf-8", { fatal: false }).decode(body).trimStart();
  // Editors write `<!-- … -->` before the root: skip each one; an unclosed one is not an SVG start.
  while (text.startsWith("<!--")) {
    const end = text.indexOf("-->");
    if (end < 0) return false;
    text = text.slice(end + "-->".length).trimStart();
  }
  return SVG_STARTS.some((start) => text.startsWith(start));
}

export type LogoType = "image/svg+xml" | "image/png" | "image/jpeg" | "image/webp";

/** What a chosen logo is by its first bytes (never its name or declared type), or `null`. */
export async function logoTypeOf(file: File): Promise<LogoType | null> {
  const head = new Uint8Array(await file.slice(0, SVG_HEAD_BYTES).arrayBuffer());
  if (hasAt(head, 0, PNG_SIGNATURE)) return "image/png";
  if (hasAt(head, 0, JPEG_SIGNATURE)) return "image/jpeg";
  if (looksRaster(head, file.size)) return "image/webp";
  return looksSvg(head) ? "image/svg+xml" : null;
}

/** Bytes per `String.fromCharCode` call: a spread of the whole image would overflow the stack. */
const CHUNK = 0x8000;

/** The base64 length of `size` bytes, padded: what the route measures. */
function encodedLength(size: number): number {
  return 4 * Math.ceil(size / 3);
}

/** `_RAW_IMAGE_TEXT_MAX`. */
const MAX_ENCODED_LENGTH = encodedLength(MAX_REFERENCE_BYTES) + 4;

function hasAt(bytes: Uint8Array, offset: number, signature: readonly number[]): boolean {
  return signature.every((byte, index) => bytes[offset + index] === byte);
}

/** Whether the server's sniffer could accept a file that starts with `head` and holds `size` bytes. */
function looksRaster(head: Uint8Array, size: number): boolean {
  if (size >= PNG_SIGNATURE.length && hasAt(head, 0, PNG_SIGNATURE)) return true;
  if (size >= JPEG_SIGNATURE.length && hasAt(head, 0, JPEG_SIGNATURE)) return true;
  if (size < MIN_WEBP_BYTES || !hasAt(head, 0, RIFF) || !hasAt(head, 8, WEBP)) return false;
  const declared = new DataView(head.buffer, head.byteOffset, head.byteLength).getUint32(4, true);
  return declared + 8 === size;
}

function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let at = 0; at < bytes.length; at += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(at, at + CHUNK));
  }
  return btoa(binary);
}

/** The wire form of an image the server could store, or why it certainly would not. */
export async function readReferenceFile(
  file: File,
  options: { readonly svg?: boolean } = {},
): Promise<ReferenceFileRead> {
  if (encodedLength(file.size) > MAX_ENCODED_LENGTH) return { kind: "refused", reason: "tooBig" };
  try {
    const head = new Uint8Array(await file.slice(0, SVG_HEAD_BYTES).arrayBuffer());
    const accepted = looksRaster(head, file.size) || (options.svg === true && looksSvg(head));
    if (!accepted) return { kind: "refused", reason: "notSupported" };
    return { kind: "ok", base64: toBase64(new Uint8Array(await file.arrayBuffer())) };
  } catch {
    return { kind: "refused", reason: "unreadable" };
  }
}

/** `clean_text(raw, multiline=False)`: what the server keeps of an alt text. */
const REMOVED = /[\p{Cc}\p{Cf}\p{Cs}\p{Zl}\p{Zp}]/gu;

function codePoints(text: string): number {
  return Array.from(text).length;
}

/** Why the server would refuse this alt text, or `null` when it would keep it. */
export function altTextProblem(raw: string): AltTextProblem | null {
  if (codePoints(raw) > MAX_ALT_TEXT_CHARS * RAW_HEADROOM_FACTOR) return "tooLong";
  const cleaned = raw.replace(REMOVED, "").normalize("NFKC").trim();
  if (cleaned === "") return "required";
  return codePoints(cleaned) > MAX_ALT_TEXT_CHARS ? "tooLong" : null;
}
